import { FocusMonitor } from '@angular/cdk/a11y';
import { coerceBooleanProperty } from '@angular/cdk/coercion';
import { Component, ElementRef, Input, OnDestroy, Optional, Self, HostBinding, forwardRef, Renderer, Renderer2 } from '@angular/core';
import { FormBuilder, FormGroup, NgControl, ReactiveFormsModule, NG_VALUE_ACCESSOR, ControlValueAccessor } from '@angular/forms';
import { MatFormFieldControl } from '@angular/material';
import { Subject } from 'rxjs/Subject';
import { getLocaleDateTimeFormat } from '@angular/common';
import { element } from 'protractor';
import { SelectorMatcher } from '@angular/compiler';

/**
 * DateInput: This is a custom composite control created to enable strict editing of dates.
 * This is to replace the weak support for the date attribute on html elements.
 * @see https://material.angular.io/guide/creating-a-custom-form-field-control
 *  */

@Component({
  selector: 'app-date-input',
  templateUrl: './date-input.component.html',
  styleUrls: ['./date-input.component.css'],
  providers: [{ provide: MatFormFieldControl, useExisting: DateInputComponent }, FormBuilder]
})
export class DateInputComponent implements MatFormFieldControl<string>, OnDestroy, ControlValueAccessor {
  static nextId = 0;
  @HostBinding() id = `date-input-${DateInputComponent.nextId++}`;
  @HostBinding('attr.aria-describedby') describedBy = '';
  parts: FormGroup;
  day: string;
  month: string;
  year: string;
  stateChanges = new Subject<void>();
  focused = false;
  errorState = false;
  _placeholder: string;
  _onChange: (_: any) => void;
  _onTouched: any;
  _required = false;
  _disabled = false;
  _value: string;

  constructor(fb: FormBuilder, private fm: FocusMonitor, private elRef: ElementRef,
    @Optional() @Self() public ngControl: NgControl, private renderer: Renderer2) {

    /**
    * The FormBuilder provides syntactic sugar that shortens creating instances of a FormControl
    * FormGroup, or FormArray. It reduces the amount of boilerplate needed to build complex forms.
    * The group (a formGroup) Tracks the value and validity state of a group of FormControl instances.
    */
    this.parts = fb.group({
      'day': 'dd',
      'month': 'mm',
      'year': 'yyyy',
    });

    /** Defines an interface that acts as a bridge between the Angular forms API and a native element in the DOM. */
    if (this.ngControl != null) {
      this.ngControl.valueAccessor = this;
    }

    /** Monitors the focus of the entire control and triggers a state change when the focus changes */
    fm.monitor(elRef.nativeElement, true).subscribe(origin => {
      this.focused = !!origin;
      this.stateChanges.next();
      this._onChange(this.value);
    });

  }

  /** When this control is bound, the value of the binding will be passed in here */
  writeValue(value: any): void {
    this.renderer.setProperty(this.elRef.nativeElement, 'value', value);
    this.value = value;
  }

  @Input()
  get value(): string | null {
    const n = this.parts.value;
    if (n.day.length === 2 && n.month.length === 2 && n.year.length === 4) {
      if (n.day.toLowerCase() !== 'dd' && n.month.toLowerCase() !== 'mm' && n.year.toLowerCase() !== 'yyyy') {
        this._value = n.day + '/' + n.month + '/' + n.year;
        return this._value;
      }
    }
    return null;
  }
  set value(dateInput: string | null) {
    if (this._onChange) {
      this._onChange(this.value);
    }

    if (dateInput === undefined || dateInput === null || dateInput.length === 0) {
      return;
    }

    if (!this.isValidDate(dateInput)) {
      return;
    }

    /** Split the date */
    const dateParts = dateInput.split('/');
    if (dateParts.length > 2) {
      this.day = dateParts[0];
      this.month = dateParts[1];
      this.year = dateParts[2];
    }

    this._value = dateInput;
    this.parts.setValue({ day: this.day, month: this.month, year: this.year });
    this.stateChanges.next();
  }

  /** Called by the angular api */
  registerOnChange(fn: (_: any) => void): void {
    this._onChange = fn;
  }
  /** Called by the angular api */
  registerOnTouched(fn: any): void {
    this._onTouched = fn;
  }

  /** Called when this object is destroyed */
  ngOnDestroy() {
    this.stateChanges.complete();
    this.fm.stopMonitoring(this.elRef.nativeElement);
  }

  /** Binds to the host container. If this.focused or !this.empty then float the hint */
  @HostBinding('class.floating')
  get shouldLabelFloat() {
    // return this.focused || !this.empty;
    return true;
  }

  setDescribedByIds(ids: string[]) {
    this.describedBy = ids.join(' ');
  }

  @Input()
  get required() {
    return this._required;
  }
  set required(req) {
    this._required = coerceBooleanProperty(req);
    this.stateChanges.next();
  }

  @Input()
  get disabled(): boolean { return this._disabled; }
  set disabled(value: boolean) {
    this._disabled = coerceBooleanProperty(value);
    this._disabled ? this.parts.disable() : this.parts.enable();
    this.stateChanges.next();
  }

  @Input()
  get placeholder() {
    return this._placeholder;
  }
  set placeholder(plh) {
    this._placeholder = plh;
    this.stateChanges.next();
  }

  get empty() {
    const n = this.parts.value;
    return !n.day && !n.month && !n.year;
  }

  onContainerClick(event: MouseEvent) {
    if ((event.target as Element).tagName.toLowerCase() !== 'input') {
      this.elRef.nativeElement.querySelector('input').focus();
    }

  }

  /** Fired whenever the input is changed on the component. This traps any events we need to respond to. */
  keyUp(event: any) {

    if ((event.keyCode !== 8 || event.keyCode !== 16 || event.keyCode !== 9) && (event.keyCode < 96 || event.keyCode > 105)) {
      if (event.keyCode !== 8 && event.keyCode !== 9 && (event.keyCode < 46 || event.keyCode > 57)) {
        event.preventDefault();
        return;
      }
    }

    const elementId = event.srcElement.id;
    if (elementId === 'day' && event.keyCode !== 16 && event.keyCode !== 9
      && event.keyCode !== 8 && event.keyCode !== 46 && event.srcElement.value.length > 1) {
      document.getElementById('month').focus();
    }

    if (elementId === 'month' && event.keyCode !== 16 && event.keyCode !== 9
      && event.keyCode !== 8 && event.keyCode !== 46 && event.srcElement.value.length > 1) {
      document.getElementById('year').focus();
    }

    this.checkState(event);
  }

  /** Checks the state of the control */
  private checkState(event: any | null) {
    const foundElement = event.srcElement;

    /** Set errorstate if required */
    if (this.parts.touched && this.value === null) {
      this.errorState = true;
      this.Change();
      return;
    }

    if (this.parts.touched && this.value !== null) {
      if (!this.isValidDate(this.value)) {
        this.errorState = true;
        this.Change();
        return;
      }
    }

    this.errorState = false;
    this.Change();
  }

  private Change() {
    this._onChange(this.value);
    this.stateChanges.next();
  }

  /**
 * Get the number of days in any particular month
 * @link https://stackoverflow.com/a/1433119/1293256
 * @param  {integer} m The month (valid: 0-11)
 * @param  {integer} y The year
 * @return {integer}   The number of days in the month
 */
  daysInMonth(m, y) {
    switch (m) {
      case 1:
        return (y % 4 === 0 && y % 100) || y % 400 === 0 ? 29 : 28;
      case 8: case 3: case 5: case 10:
        return 30;
      default:
        return 31;
    }
  }

  /**
  * Check if a date is valid
  * @link https://stackoverflow.com/a/1433119/1293256
  * @param  {[type]}  d The day
  * @param  {[type]}  m The month
  * @param  {[type]}  y The year
  * @return {Boolean}   Returns true if valid
  */
  isValidDate(dateString: string) {
    let d;
    let m;
    let y;

    const dateParts = dateString.split('/');
    if (dateParts.length > 2) {
      d = dateParts[0];
      m = dateParts[1];
      y = dateParts[2];
    }

    if (y < 1900) {
      return false;
    }

    m = parseInt(m, 10) - 1;
    return m >= 0 && m < 12 && d > 0 && d <= this.daysInMonth(m, y);
  }

  /** Fired when a key is pressed down */
  keyDown(event: any) {
    // The next thing to do is to only allow though numeric values
    // numeric keypad or top row
    if ((event.keyCode !== 8 || event.keyCode !== 16 || event.keyCode !== 9) && (event.keyCode < 96 || event.keyCode > 105)) {
      if (event.keyCode !== 8 && event.keyCode !== 9 && (event.keyCode < 46 || event.keyCode > 57)) {
        if (event.keyCode < 37 || event.keyCode > 40) {
          event.preventDefault();
          return;
        }
      }
    }
  }

  /** Fired when any control in the composite control receives the focus */
  gotFocus(event: any) {
    if (event.srcElement.value === 'dd' || event.srcElement.value === 'mm' || event.srcElement.value === 'yyyy') {
      event.srcElement.value = '';
    }
    this.selectAll(event);
  }

  selectAll(event: any) {
    // See https://developer.microsoft.com/en-us/microsoft-edge/platform/issues/8229660/
    if (navigator.userAgent.match(/Edge\/(13|14|15|16|17)/)) {
      return setTimeout(event.target.select.bind(event.target), 10);
    }
    event.target.select();
  }

  /** Fired when the focus is lost from any of the controls that
   * make up this control, that is, any of the dd/mm/yyy controls
  */
  blur(event: any) {
    this.checkState(event);
  }
}



// WEBPACK FOOTER //
// ./src/app/components/date-input/date-input.component.ts